import { useMemo } from 'react';

import { useAppConfig } from '@/hooks/useAppConfig';

import { usePMyFunctionalPermissionsQuery } from './graphql/queries/generated/p_MyFunctionalPermissions.productivity';
import { FunctionalPermissionKey } from './types';

export const useFunctionalPermissions = () => {
  const [{ data, fetching, error }] = usePMyFunctionalPermissionsQuery({
    requestPolicy: 'cache-first',
  });

  return useMemo(() => {
    const permissions = data?.myFunctionalPermissions?.filter(
      (permission): permission is string => !!permission,
    );

    const hasPermission = (permission: FunctionalPermissionKey) => {
      // While loading or on transport error we cannot know; don't lock the user out.
      if (!permissions) {
        return true;
      }
      return permissions.includes(permission);
    };

    return { fetching, error, hasPermission };
  }, [data, fetching, error]);
};

export const useFunctionalPermission = (permission: FunctionalPermissionKey) => {
  const { hasPermission } = useFunctionalPermissions();

  return hasPermission(permission);
};

/**
 * Resolves an operation the way the rest of the app gates behaviour: unified
 * sidebar tenants are governed by the functional permission, every other tenant
 * keeps running whatever legacy check was already in place. Pass `true` for
 * `legacyAccess` where there was no legacy check, so those tenants are unaffected.
 */
export const useEffectivePermission = (
  permission: FunctionalPermissionKey,
  legacyAccess: boolean = true,
) => {
  const { hasPermission } = useFunctionalPermissions();
  const {
    ui: { isUnifiedSidebar },
  } = useAppConfig();

  return isUnifiedSidebar ? hasPermission(permission) : legacyAccess;
};

export { FunctionalPermissionKey };
